import * as React from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ExpandMore } from "@mui/icons-material";
import * as Iconoir from "iconoir-react";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import useEnsureUserProfile from "@/hooks/use-ensure-user-profile";

import { cn, getUserInitials } from "@/lib/utils";
import {
  parsePermissions,
  parseRoles,
  filterRoutesByPermission,
  NavigationRoute,
} from "@/lib/permission";

import { MENU_ITEMS } from "../../menus/menu";

function AppTitle({ isCollapsed }: { isCollapsed: boolean }) {
  return (
    <div
      className={cn(
        "aero:flex aero:items-center  aero:h-6 aero:pl-1.5",
        isCollapsed ? "aero:justify-center aero:flex-col" : "aero:gap-[6px]",
        "aero:transition-all aero:duration-200",
      )}
    >
      <div className="aero:flex aero:items-center aero:justify-center aero:w-6 aero:h-6 aero:bg-brand-primary-50 aero:rounded-[4.8px]">
        <Iconoir.CenterAlign className="aero:w-4 aero:h-4 aero:text-brand-primary-600" />
      </div>
      <span
        className={cn(
          isCollapsed ? "aero:text-[9px]" : "aero:text-base",
          "aero:font-bold aero:leading-[20px] aero:text-foreground",
        )}
      >
        Planner
      </span>
    </div>
  );
}

function ProfileSection({
  isCollapsed,
  userName,
  userRoles,
}: {
  isCollapsed: boolean;
  userName: string;
  userRoles: string[];
}) {
  const initials = getUserInitials(userName);

  return (
    <div
      className={cn(
        "aero:flex aero:items-center aero:gap-3",
        isCollapsed ? "aero:justify-center" : "aero:justify-start",
      )}
    >
      <Avatar
        className={cn(
          "aero:shrink-0",
          isCollapsed
            ? "aero:w-[44px] aero:h-[44px]"
            : "aero:w-[44px] aero:h-[44px]",
        )}
      >
        <AvatarImage src={"fallback"} alt={userName} />
        <AvatarFallback className="aero:bg-brand-primary-400 aero:text-neutral-50 aero:text-[14px] aero:font-bold">
          {initials}
        </AvatarFallback>
      </Avatar>
      {!isCollapsed && (
        <div className="aero:flex aero:flex-col aero:gap-1 aero:flex-1 aero:min-w-0">
          <span className="aero:text-[14px] aero:font-medium aero:leading-[20px] aero:text-foreground aero:whitespace-normal aero:break-words">
            {userName}
          </span>
          <span className="aero:text-[10px] aero:uppercase aero:tracking-[0.4px] aero:leading-[12px] aero:text-neutral-500 aero:whitespace-normal aero:break-words">
            {userRoles.join(", ")}
          </span>
        </div>
      )}
    </div>
  );
}

function ToggleButton({
  isCollapsed,
  onClick,
}: {
  isCollapsed: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "aero:flex aero:items-center aero:justify-center aero:rounded-[8px] aero:cursor-pointer aero:shrink-0 aero:border aero:border-neutral-200 aero:bg-brand-primary-50 aero:p-1.5",
      )}
    >
      {isCollapsed ? (
        <ChevronRight className="aero:w-4 aero:h-4 aero:text-neutral-600" />
      ) : (
        <ChevronLeft className="aero:w-4 aero:h-4 aero:text-neutral-600" />
      )}
    </button>
  );
}

function buildMenuUrl(parentUrl: string, itemUrl: string) {
  return `${parentUrl}${itemUrl || ""}`;
}

function hasActiveDescendant(
  item: NavigationRoute,
  parentUrl: string,
  isActiveRoute: (url: string) => boolean,
): boolean {
  const currentUrl = buildMenuUrl(parentUrl, item.url);

  if (item.children?.length) {
    return item.children.some((child) =>
      hasActiveDescendant(child, currentUrl, isActiveRoute),
    );
  }

  return isActiveRoute(currentUrl);
}

function getNestedIndentClass(level: number) {
  if (level <= 0) return "";
  if (level === 1) return "aero:ml-6";
  if (level === 2) return "aero:ml-[-8px]";
  return "aero:ml-14";
}

function getSubMenuVerticalPositionClass(level: number) {
  if (level >= 2) {
    return "aero:ml-6";
  }

  return "";
}

// A tree "trunk" line naturally wants to span the full height of its
// children container, but the elbow connectors only reach the *middle* of
// each row — so a line sized to the container's full height overshoots past
// the last child's connector and leaves a dangling stub below it. This hook
// measures where the last child's own row actually sits (via `lastRowRef`,
// which must be attached only to that row, not to its own expanded
// children if it has any) and keeps the trunk's height in sync as the tree
// expands/collapses.
function useTrunkHeight() {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const lastRowRef = React.useRef<HTMLDivElement>(null);
  const [height, setHeight] = React.useState<number | null>(null);

  const recalcHeight = React.useCallback(() => {
    if (!containerRef.current || !lastRowRef.current) return;
    const containerTop = containerRef.current.getBoundingClientRect().top;
    const rowRect = lastRowRef.current.getBoundingClientRect();
    setHeight(rowRect.top + rowRect.height / 2 - containerTop);
  }, []);

  React.useLayoutEffect(() => {
    recalcHeight();
  });

  React.useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(() => recalcHeight());
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [recalcHeight]);

  return { containerRef, lastRowRef, height };
}

function SubMenuItemExpanded({
  item,
  parentUrl,
  level,
  isActive,
  isActiveRoute,
  rowRef,
}: {
  item: NavigationRoute;
  parentUrl: string;
  level: number;
  isActive: boolean;
  isActiveRoute: (url: string) => boolean;
  // Set on the last sibling so the parent trunk line can measure exactly
  // where its own row (not its expanded children, if any) sits.
  rowRef?: React.Ref<HTMLDivElement>;
}) {
  const currentUrl = buildMenuUrl(parentUrl, item.url);
  const hasChildren = Boolean(item.children?.length);
  const isChildActive = hasChildren
    ? item.children!.some((child) =>
        hasActiveDescendant(child, currentUrl, isActiveRoute),
      )
    : false;
  const [isOpen, setIsOpen] = React.useState(isChildActive);
  const showConnector = level > 0;

  React.useEffect(() => {
    if (isChildActive) {
      setIsOpen(true);
    }
  }, [isChildActive]);

  const isCurrentActive = hasChildren ? isChildActive : isActive;
  const {
    containerRef: nestedTrunkContainerRef,
    lastRowRef: nestedTrunkLastRowRef,
    height: nestedTrunkHeight,
  } = useTrunkHeight();

  if (hasChildren) {
    return (
      <SidebarMenuItem className={cn(getNestedIndentClass(level))}>
        <Collapsible
          open={isOpen}
          onOpenChange={setIsOpen}
          className="aero:group/collapsible"
        >
          <div className="aero:relative" ref={rowRef}>
            {showConnector && (
              <div
                className="aero:absolute aero:left-0 aero:w-[13px] aero:h-[8px] aero:border-l-2 aero:border-b-2 aero:border-neutral-200 aero:rounded-bl-[8px]"
                style={{ top: "calc(50% - 4px)" }}
              />
            )}
            <CollapsibleTrigger
              className={cn(
                "aero:flex aero:w-full aero:items-center aero:rounded-[4px] aero:cursor-pointer aero:py-2 aero:px-3 aero:gap-2",
                "aero:transition-all aero:duration-200",
                "aero:hover:bg-brand-primary-50",
                showConnector && "aero:ml-4",
                isCurrentActive ? "aero:bg-brand-primary-50" : "aero:bg-transparent",
              )}
            >
              {item.icon && (
                <item.icon
                  className={cn(
                    "aero:w-4 aero:h-4 aero:shrink-0",
                    isCurrentActive
                      ? "aero:text-brand-primary-600"
                      : "aero:text-neutral-600",
                  )}
                />
              )}
              <span
                className={cn(
                  "aero:flex-1 aero:text-[14px] aero:leading-[20px] aero:text-left aero:font-['Inter',sans-serif]",
                  isCurrentActive
                    ? "aero:font-bold aero:text-brand-primary-600"
                    : "aero:font-normal aero:text-neutral-600",
                )}
              >
                {item.title}
              </span>
              <ExpandMore
                className={cn(
                  "aero:w-4 aero:h-4 aero:shrink-0 aero:transition-transform aero:duration-200",
                  isOpen && "aero:rotate-180",
                  isCurrentActive
                    ? "aero:text-brand-primary-600"
                    : "aero:text-neutral-600",
                )}
              />
            </CollapsibleTrigger>
          </div>

          <CollapsibleContent>
            <div
              className={cn(
                "aero:relative aero:mt-1 aero:space-y-1 aero:pl-2",
                getSubMenuVerticalPositionClass(level + 1),
              )}
              ref={nestedTrunkContainerRef}
            >
              <div
                className="aero:absolute aero:left-0 aero:top-0 aero:w-[2px] aero:bg-neutral-200"
                style={{
                  height:
                    nestedTrunkHeight != null
                      ? `${nestedTrunkHeight}px`
                      : "calc(100% - 18px)",
                }}
              />
              {item.children!.map((child, index) => {
                const childUrl = buildMenuUrl(currentUrl, child.url);
                return (
                  <SubMenuItemExpanded
                    key={child.title}
                    item={child}
                    parentUrl={currentUrl}
                    level={level + 1}
                    isActive={isActiveRoute(childUrl)}
                    isActiveRoute={isActiveRoute}
                    rowRef={
                      index === item.children!.length - 1
                        ? nestedTrunkLastRowRef
                        : undefined
                    }
                  />
                );
              })}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </SidebarMenuItem>
    );
  }

  return (
    <div
      className={cn(
        "aero:relative aero:flex aero:items-center aero:min-h-[36px]",
        getNestedIndentClass(level),
      )}
      ref={rowRef}
    >
      {showConnector && (
        <div
          className="aero:absolute aero:left-0 aero:w-[13px] aero:h-[8px] aero:border-l-2 aero:border-b-2 aero:border-neutral-200 aero:rounded-bl-[8px]"
          style={{ top: "calc(50% - 4px)" }}
        />
      )}
      <Link to={currentUrl} className={cn("aero:flex-1", showConnector && "aero:ml-4")}>
        <div
          className={cn(
            "aero:flex aero:items-center aero:rounded-[4px] aero:cursor-pointer aero:py-2 aero:px-3",
            "aero:transition-all aero:duration-200",
            "aero:hover:bg-brand-primary-50",
            isActive ? "aero:bg-brand-primary-50" : "aero:bg-transparent",
          )}
        >
          {item.icon && (
            <item.icon
              className={cn(
                "aero:w-4 aero:h-4 aero:shrink-0 aero:mr-2",
                isActive ? "aero:text-brand-primary-600" : "aero:text-neutral-600",
              )}
            />
          )}
          <span
            className={cn(
              "aero:flex-1 aero:text-[14px] aero:leading-[20px] aero:font-['Inter',sans-serif]",
              isActive
                ? "aero:font-bold aero:text-brand-primary-600"
                : "aero:font-normal aero:text-neutral-600",
            )}
          >
            {item.title}
          </span>
        </div>
      </Link>
    </div>
  );
}

function MenuGroupExpanded({
  item,
  parentUrl = "",
  isActiveRoute,
}: {
  item: NavigationRoute;
  parentUrl?: string;
  isActiveRoute: (url: string) => boolean;
}) {
  const currentUrl = buildMenuUrl(parentUrl, item.url);
  const hasChildren = item.children && item.children.length > 0;
  const isChildActive = hasChildren
    ? item.children!.some((child) =>
        hasActiveDescendant(child, currentUrl, isActiveRoute),
      )
    : false;
  const [isOpen, setIsOpen] = React.useState(isChildActive);

  React.useEffect(() => {
    if (isChildActive) {
      setIsOpen(true);
    }
  }, [isChildActive]);

  const isActive = hasChildren ? isChildActive : isActiveRoute(currentUrl);

  const Icon = item.icon;

  const { containerRef: trunkContainerRef, lastRowRef, height: trunkHeight } =
    useTrunkHeight();

  if (hasChildren) {
    return (
      <SidebarMenuItem>
        <Collapsible
          open={isOpen}
          onOpenChange={setIsOpen}
          className="aero:group/collapsible aero:w-full"
        >
          <CollapsibleTrigger
            className={cn(
              "aero:flex aero:w-full aero:items-center aero:rounded-[4px] aero:cursor-pointer aero:py-2.5 aero:px-3 aero:gap-3",
              "aero:transition-all aero:duration-200",
              "aero:hover:bg-brand-primary-50 aero:hover:scale-[1.02] aero:origin-left",
              isActive ? "aero:bg-brand-primary-50" : "aero:bg-transparent",
            )}
          >
            {Icon && (
              <Icon
                className={cn(
                  "aero:w-5 aero:h-5 aero:shrink-0",
                  isActive
                    ? "aero:text-brand-primary-600"
                    : "aero:text-neutral-600",
                )}
              />
            )}
            <span
              className={cn(
                "aero:flex-1 aero:text-[14px] aero:leading-[20px] aero:text-left aero:font-['Inter',sans-serif]",
                isActive
                  ? "aero:font-bold aero:text-brand-primary-600"
                  : "aero:font-normal aero:text-neutral-600",
              )}
            >
              {item.title}
            </span>
            <ExpandMore
              className={cn(
                "aero:w-5 aero:h-5 aero:shrink-0 aero:transition-transform aero:duration-200",
                isOpen && "aero:rotate-180",
                isActive
                  ? "aero:text-brand-primary-600"
                  : "aero:text-neutral-600",
              )}
            />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div
              className="aero:relative aero:flex aero:flex-col aero:mt-1.5"
              ref={trunkContainerRef}
            >
              <div
                className="aero:absolute aero:left-[24px] aero:top-0 aero:w-[2px] aero:bg-neutral-200"
                style={{
                  height:
                    trunkHeight != null ? `${trunkHeight}px` : "calc(100% - 18px)",
                }}
              />
              {item.children!.map((child, index) => (
                <SubMenuItemExpanded
                  key={child.title}
                  item={child}
                  parentUrl={currentUrl}
                  level={1}
                  isActive={isActiveRoute(buildMenuUrl(currentUrl, child.url))}
                  isActiveRoute={isActiveRoute}
                  rowRef={
                    index === item.children!.length - 1 ? lastRowRef : undefined
                  }
                />
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      <Link
        to={currentUrl || "#"}
        className={cn(
          "aero:flex aero:w-full aero:items-center aero:rounded-[4px] aero:cursor-pointer aero:py-2.5 aero:px-3 aero:gap-3",
          "aero:transition-all aero:duration-200",
          "aero:hover:bg-brand-primary-50 aero:hover:scale-[1.02] aero:origin-left",
          isActive ? "aero:bg-brand-primary-50" : "aero:bg-transparent",
        )}
      >
        {Icon && (
          <Icon
            className={cn(
              "aero:w-5 aero:h-5 aero:shrink-0",
              isActive
                ? "aero:text-brand-primary-600"
                : "aero:text-neutral-600",
            )}
          />
        )}
        <span
          className={cn(
            "aero:flex-1 aero:text-[14px] aero:leading-[20px] aero:font-['Inter',sans-serif]",
            isActive
              ? "aero:font-bold aero:text-brand-primary-600"
              : "aero:font-normal aero:text-neutral-600",
          )}
        >
          {item.title}
        </span>
      </Link>
    </SidebarMenuItem>
  );
}

function SubMenuItemCollapsed({
  item,
  parentUrl,
  level,
  isActive,
  isActiveRoute,
}: {
  item: NavigationRoute;
  parentUrl: string;
  level: number;
  isActive: boolean;
  isActiveRoute: (url: string) => boolean;
}) {
  const currentUrl = buildMenuUrl(parentUrl, item.url);
  const hasChildren = Boolean(item.children?.length);

  if (hasChildren) {
    const isChildActive = item.children!.some((child) =>
      hasActiveDescendant(child, currentUrl, isActiveRoute),
    );

    return (
      <div className={cn("aero:flex aero:flex-col aero:gap-1", getNestedIndentClass(level))}>
        <div
          className={cn(
            "aero:flex aero:items-center aero:rounded-[4px] aero:py-2 aero:px-3",
            isChildActive ? "aero:bg-brand-primary-50" : "aero:bg-transparent",
          )}
        >
          {item.icon && (
            <item.icon
              className={cn(
                "aero:w-4 aero:h-4 aero:mr-2",
                isChildActive
                  ? "aero:text-brand-primary-600"
                  : "aero:text-neutral-600",
              )}
            />
          )}
          <span
            className={cn(
              "aero:text-[14px] aero:leading-[20px] aero:font-['Inter',sans-serif]",
              isChildActive
                ? "aero:font-bold aero:text-brand-primary-600"
                : "aero:font-normal aero:text-neutral-600",
            )}
          >
            {item.title}
          </span>
        </div>

        <div className="aero:border-l-2 aero:border-neutral-200 aero:pl-2 aero:space-y-1">
          {item.children!.map((child) => {
            const childUrl = buildMenuUrl(currentUrl, child.url);
            return (
              <SubMenuItemCollapsed
                key={child.title}
                item={child}
                parentUrl={currentUrl}
                level={level + 1}
                isActive={isActiveRoute(childUrl)}
                isActiveRoute={isActiveRoute}
              />
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <Link to={currentUrl} className={cn(getNestedIndentClass(level))}>
      <div
        className={cn(
          "aero:flex aero:items-center aero:rounded-[4px] aero:cursor-pointer aero:h-9 aero:py-2 aero:px-3",
          "aero:transition-all aero:duration-200",
          "aero:hover:bg-brand-primary-50 aero:hover:scale-[1.02] aero:origin-left",
          isActive ? "aero:bg-brand-primary-50" : "aero:bg-transparent",
        )}
      >
        <span
          className={cn(
            "aero:text-[14px] aero:leading-[20px] aero:font-['Inter',sans-serif]",
            isActive
              ? "aero:font-bold aero:text-brand-primary-600"
              : "aero:font-normal aero:text-neutral-600",
          )}
        >
          {item.title}
        </span>
      </div>
    </Link>
  );
}

function MenuItemCollapsed({
  item,
  isActive,
  isActiveRoute,
}: {
  item: NavigationRoute;
  isActive: boolean;
  isActiveRoute: (url: string) => boolean;
}) {
  const [isHovered, setIsHovered] = React.useState(false);
  const hasChildren = item.children && item.children.length > 0;
  const Icon = item.icon;

  if (hasChildren) {
    return (
      <SidebarMenuItem>
        <Popover open={isHovered} onOpenChange={setIsHovered}>
          <PopoverTrigger asChild>
            <button
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              className={cn(
                "aero:flex aero:items-center aero:justify-center aero:cursor-pointer",
                "aero:px-3 aero:py-2.5 aero:rounded-[8px]",
                "aero:transition-all aero:duration-200",
                "aero:hover:bg-brand-primary-50 aero:hover:scale-[1.02]",
                isActive ? "aero:bg-brand-primary-50" : "aero:bg-transparent",
              )}
            >
              {Icon && (
                <Icon
                  className={cn(
                    "aero:w-5 aero:h-5 aero:shrink-0",
                    isActive
                      ? "aero:text-brand-primary-600"
                      : "aero:text-neutral-500",
                  )}
                />
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent
            side="right"
            align="start"
            sideOffset={8}
            className="aero:p-2.5 aero:mx-0 aero:min-w-[160px] aero:rounded-[4px] aero:bg-white aero:border aero:border-brand-primary-600 aero:shadow-[0px_4px_4px_rgba(43,43,43,0.25)]"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <div className="aero:flex aero:flex-col aero:justify-center">
              <span className="aero:text-[14px] aero:font-bold aero:leading-[20px] aero:text-brand-primary-600">
                {item.title}
              </span>
            </div>
            <div className="aero:w-full aero:h-px aero:bg-neutral-300 aero:my-2.5" />
            <div className="aero:flex aero:flex-col aero:gap-2.5">
              {item.children!.map((child) => (
                <SubMenuItemCollapsed
                  key={child.title}
                  item={child}
                  parentUrl={item.url}
                  level={1}
                  isActive={isActiveRoute(buildMenuUrl(item.url, child.url))}
                  isActiveRoute={isActiveRoute}
                />
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              to={item.url || "#"}
              className={cn(
                "aero:flex aero:items-center aero:justify-center aero:cursor-pointer",
                "aero:px-3 aero:py-2.5 aero:rounded-[8px]",
                "aero:transition-all aero:duration-200",
                "aero:hover:bg-brand-primary-50 aero:hover:scale-[1.1]",
                isActive ? "aero:bg-brand-primary-50" : "aero:bg-transparent",
              )}
            >
              {Icon && (
                <Icon
                  className={cn(
                    "aero:w-5 aero:h-5 aero:shrink-0",
                    isActive
                      ? "aero:text-brand-primary-600"
                      : "aero:text-neutral-500",
                  )}
                />
              )}
            </Link>
          </TooltipTrigger>
          <TooltipContent
            side="right"
            sideOffset={8}
            hideArrow
            className="aero:p-2.5 aero:rounded-[4px] aero:bg-white aero:border aero:border-brand-primary-600 aero:shadow-[0px_4px_4px_rgba(43,43,43,0.25)]"
          >
            <span className="aero:text-[14px] aero:font-bold aero:leading-[20px] aero:text-brand-primary-600">
              {item.title}
            </span>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </SidebarMenuItem>
  );
}

export function AppSidebar() {
  const { userProfile, isLoading } = useEnsureUserProfile();
  const routerState = useRouterState();
  const activeUrl = routerState.location.pathname;
  const { open, setOpen, state } = useSidebar();
  const isCollapsed = state === "collapsed";

  const userRoles = React.useMemo(
    () => parseRoles(userProfile?.roles),
    [userProfile?.roles],
  );

  const filteredMenuItems = React.useMemo(() => {
    const userPermissions = parsePermissions(userProfile?.permissionNumbers);
    const userStringPermissions = userProfile?.rolePermissions || [];
    return filterRoutesByPermission(
      MENU_ITEMS,
      userPermissions,
      userStringPermissions,
      userRoles,
    );
  }, [userProfile?.permissionNumbers, userProfile?.rolePermissions, userRoles]);

  const isActiveRoute = (url: string) => {
    // Empty URL should not match any route - parent menus with empty URL
    // should only be active based on their children's active state
    if (!url) return false;
    return activeUrl === url || activeUrl.startsWith(url + "/");
  };

  const displayName =
    (isLoading && "Loading...") ||
    userProfile?.fullName ||
    userProfile?.userName ||
    "User";

  const displayRoles = userRoles.length > 0 ? userRoles : ["User"];

  return (
    <Sidebar collapsible="icon" className="aero:bg-white">
      <SidebarHeader className="aero:px-6 aero:pt-6 aero:pb-3 aero:gap-3">
        <AppTitle isCollapsed={isCollapsed} />

        <div
          className={cn(
            isCollapsed ? "aero:w-6 aero:mx-auto" : "aero:w-51",
            "aero:h-[2px] aero:bg-neutral-200 aero:rounded-[2px] aero:shrink-0",
            "aero:transition-all aero:duration-300 aero:ease-in-out",
          )}
        />

        <div
          className={cn(
            "aero:flex aero:items-center aero:gap-6  aero:transition-all aero:duration-300 aero:ease-in-out aero:shrink-0",
            isCollapsed
              ? "aero:flex-col aero:justify-center"
              : "aero:flex-row aero:justify-between",
          )}
        >
          {isCollapsed ? (
            <Tooltip>
              <TooltipTrigger>
                <ProfileSection
                  isCollapsed={isCollapsed}
                  userName={displayName}
                  userRoles={displayRoles}
                />
              </TooltipTrigger>
              <TooltipContent
                side="right"
                sideOffset={12}
                hideArrow
                className="aero:flex aero:flex-col aero:rounded-[4px] aero:bg-white aero:border aero:border-neutral-200 aero:shadow-md aero:p-2.5 aero:max-w-[240px]"
              >
                <span className="aero:font-medium aero:text-[14px] aero:leading-[20px] aero:text-brand-primary-600 aero:whitespace-normal aero:break-words">
                  {displayName}
                </span>
                <span className="aero:text-[14px] aero:leading-[20px] aero:text-neutral-600 aero:whitespace-normal aero:break-words">
                  {displayRoles.join(", ")}
                </span>
              </TooltipContent>
            </Tooltip>
          ) : (
            <ProfileSection
              isCollapsed={isCollapsed}
              userName={displayName}
              userRoles={displayRoles}
            />
          )}
          <ToggleButton
            isCollapsed={isCollapsed}
            onClick={() => setOpen(!open)}
          />
        </div>

        <div
          className={cn(
            isCollapsed ? "aero:w-6 aero:mx-auto" : "aero:w-51",
            "aero:h-[2px] aero:bg-neutral-200 aero:rounded-[2px] aero:shrink-0 ",
            "aero:transition-all aero:duration-300 aero:ease-in-out",
          )}
        />
      </SidebarHeader>
      <SidebarContent className="aero:px-6 aero:pb-6 ">
        <SidebarGroup className="aero:p-0">
          <SidebarGroupContent>
            <SidebarMenu
              className={cn(
                "aero:flex aero:flex-col aero:gap-2",
                isCollapsed && "aero:items-center",
              )}
            >
              {filteredMenuItems.map((item) => {
                const isChildActive = item.children?.some((child) =>
                  hasActiveDescendant(child, item.url, isActiveRoute),
                );
                const isActive = isChildActive || isActiveRoute(item.url);

                return isCollapsed ? (
                  <MenuItemCollapsed
                    key={item.title}
                    item={item}
                    isActive={isActive}
                    isActiveRoute={isActiveRoute}
                  />
                ) : (
                  <MenuGroupExpanded
                    key={item.title}
                    item={item}
                    isActiveRoute={isActiveRoute}
                  />
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
